import * as React from 'react'
import { CaretDown, CaretLeft, CaretRight } from '@phosphor-icons/react'
import { cn } from '../../lib/utils'
import { Button } from '../core/button'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from '../core/dropdown-menu'
import { accentBg } from '../core/badge-colors'
import { AgendaRow, relativeDayLabel } from '../core/event-agenda'
import {
  type CalendarEvent,
  type WeekStart,
  addMonths,
  eventsForDay,
  formatMonthYear,
  formatWeekdayNarrow,
  getMonthGrid,
  isSameDay,
  isSameMonth,
  startOfDay,
  startOfMonth,
} from '../core/event-calendar-utils'

/**
 * EventCalendarWidget — a compact dashboard/homepage widget: a mini month grid
 * with event dots beside/above a short agenda for the selected day. Picking a
 * day (or paging months, or jumping via the month dropdown) updates the agenda.
 *
 *   <EventCalendarWidget events={events} onEventClick={openEvent} />
 *   <EventCalendarWidget events={events} layout="horizontal" />
 *
 * `layout` places the calendar and list either stacked (`vertical`, default) or
 * side-by-side (`horizontal`, which still stacks on narrow screens). Read/
 * navigate only — it doesn't create or edit events (use the full `EventCalendar`
 * for that). Selection is controlled (`selectedDate`) or uncontrolled
 * (`defaultSelectedDate`, defaults to today).
 */

/** Up to three color dots for the events on a mini-grid day. */
const MAX_DAY_DOTS = 3

/** Short month names for the month-picker dropdown. */
const shortMonthFmt = new Intl.DateTimeFormat('en-US', { month: 'short' })
const MONTHS = Array.from({ length: 12 }, (_, i) =>
  shortMonthFmt.format(new Date(2000, i, 1))
)

export type EventCalendarWidgetLayout = 'vertical' | 'horizontal'

export interface EventCalendarWidgetProps {
  events: CalendarEvent[]
  /** `vertical` (stacked, default) or `horizontal` (calendar + list side-by-side). */
  layout?: EventCalendarWidgetLayout
  /** Selected day (controlled). */
  selectedDate?: Date
  /** Initial selected day (uncontrolled); defaults to today. */
  defaultSelectedDate?: Date
  onSelectDate?: (date: Date) => void
  /** First day of the week: 0 = Sunday (default), 1 = Monday. */
  weekStartsOn?: WeekStart
  /** "Now", for today-highlighting + Today/Tomorrow labels. Defaults to now. */
  now?: Date
  /** Called when an agenda row is clicked; omit for a read-only agenda. */
  onEventClick?: (event: CalendarEvent) => void
  /** Text shown when the selected day has no events. */
  emptyLabel?: string
  className?: string
}

export const EventCalendarWidget = React.forwardRef<
  HTMLDivElement,
  EventCalendarWidgetProps
>(
  (
    {
      events,
      layout = 'vertical',
      selectedDate: selectedProp,
      defaultSelectedDate,
      onSelectDate,
      weekStartsOn = 0,
      now,
      onEventClick,
      emptyLabel = 'No events',
      className,
    },
    ref
  ) => {
    const resolvedNow = now ?? new Date()
    // Controlled/uncontrolled selection — inlined to match the fb-ui convention
    // (each component manages its own controllable state; there's no shared hook).
    const isSelectedControlled = selectedProp !== undefined
    const [internalSelected, setInternalSelected] = React.useState(
      defaultSelectedDate ?? startOfDay(resolvedNow)
    )
    const selected = isSelectedControlled ? selectedProp : internalSelected
    const setSelected = (next: Date) => {
      if (!isSelectedControlled) setInternalSelected(next)
      onSelectDate?.(next)
    }
    const [visibleMonth, setVisibleMonth] = React.useState(startOfMonth(selected))
    const [monthMenuOpen, setMonthMenuOpen] = React.useState(false)
    const [pickerYear, setPickerYear] = React.useState(visibleMonth.getFullYear())

    const horizontal = layout === 'horizontal'
    const grid = getMonthGrid(visibleMonth, weekStartsOn)
    const selectedEvents = eventsForDay(events, selected)

    const selectDay = (day: Date) => {
      setSelected(day)
      if (!isSameMonth(day, visibleMonth)) setVisibleMonth(startOfMonth(day))
    }

    return (
      <div
        ref={ref}
        className={cn(
          'flex w-full rounded-xl border border-fbui-gray-200 bg-fbui-base-white',
          horizontal ? 'max-w-2xl flex-col sm:flex-row' : 'max-w-xs flex-col',
          className
        )}
      >
        {/* Mini month */}
        <div className={cn('p-3', horizontal && 'sm:w-72 sm:shrink-0')}>
          {/* Header band — fixed height so it shares a baseline with the agenda
              header across the divider in the horizontal layout. */}
          <div className="mb-2 flex h-7 items-center justify-between">
            <DropdownMenu
              open={monthMenuOpen}
              onOpenChange={(o) => {
                setMonthMenuOpen(o)
                if (o) setPickerYear(visibleMonth.getFullYear())
              }}
            >
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex items-center gap-1 rounded-md text-sm font-semibold text-fbui-gray-900 outline-none transition-colors hover:text-fbui-gray-700 focus-visible:ring-2 focus-visible:ring-fbui-ring-brand"
                >
                  {formatMonthYear(visibleMonth)}
                  <CaretDown className="size-3.5 text-fbui-gray-500" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56 p-3">
                <div className="mb-2 flex items-center justify-between">
                  <Button
                    variant="ghost"
                    iconOnly
                    size="xs"
                    aria-label="Previous year"
                    onClick={() => setPickerYear((y) => y - 1)}
                  >
                    <CaretLeft />
                  </Button>
                  <span className="text-sm font-semibold text-fbui-gray-900">
                    {pickerYear}
                  </span>
                  <Button
                    variant="ghost"
                    iconOnly
                    size="xs"
                    aria-label="Next year"
                    onClick={() => setPickerYear((y) => y + 1)}
                  >
                    <CaretRight />
                  </Button>
                </div>
                <div className="grid grid-cols-3 gap-1">
                  {MONTHS.map((m, i) => {
                    const isCurrent =
                      i === visibleMonth.getMonth() &&
                      pickerYear === visibleMonth.getFullYear()
                    return (
                      <button
                        key={m}
                        type="button"
                        onClick={() => {
                          setVisibleMonth(new Date(pickerYear, i, 1))
                          setMonthMenuOpen(false)
                        }}
                        className={cn(
                          'rounded-md px-2 py-1.5 text-sm outline-none transition-colors focus-visible:ring-2 focus-visible:ring-fbui-ring-brand',
                          isCurrent
                            ? 'bg-fbui-basil-600 font-medium text-fbui-base-white hover:bg-fbui-basil-700'
                            : 'text-fbui-gray-700 hover:bg-fbui-gray-100'
                        )}
                      >
                        {m}
                      </button>
                    )
                  })}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

            <div className="flex items-center gap-1">
              <Button
                variant="primary"
                appearance="outline"
                iconOnly
                size="xs"
                aria-label="Previous month"
                onClick={() => setVisibleMonth(addMonths(visibleMonth, -1))}
              >
                <CaretLeft />
              </Button>
              <Button
                variant="primary"
                appearance="outline"
                iconOnly
                size="xs"
                aria-label="Next month"
                onClick={() => setVisibleMonth(addMonths(visibleMonth, 1))}
              >
                <CaretRight />
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-7">
            {grid.slice(0, 7).map((d) => (
              <div
                key={d.getDay()}
                className="pb-1 text-center text-xs font-medium text-fbui-gray-500"
              >
                {formatWeekdayNarrow(d)}
              </div>
            ))}
            {grid.map((day) => {
              const outside = !isSameMonth(day, visibleMonth)
              const today = isSameDay(day, resolvedNow)
              const isSelected = isSameDay(day, selected)
              const dots = eventsForDay(events, day)
                .slice(0, MAX_DAY_DOTS)
                .map((e) => e.color ?? 'primary')
              return (
                <button
                  key={day.toISOString()}
                  type="button"
                  aria-pressed={isSelected}
                  aria-label={day.toDateString()}
                  onClick={() => selectDay(day)}
                  className="flex flex-col items-center gap-0.5 rounded-md py-1 outline-none transition-colors hover:bg-fbui-gray-50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-fbui-ring-brand"
                >
                  <span
                    className={cn(
                      'flex size-6 items-center justify-center rounded-full text-xs',
                      isSelected
                        ? 'bg-fbui-basil-600 font-semibold text-fbui-base-white'
                        : today
                          ? 'font-semibold text-fbui-basil-700'
                          : outside
                            ? 'text-fbui-gray-300'
                            : 'text-fbui-gray-700'
                    )}
                  >
                    {day.getDate()}
                  </span>
                  {/* Reserve the dot row so every week keeps the same height. */}
                  <span className="flex h-1.5 items-center gap-0.5">
                    {dots.map((color, i) => (
                      <span
                        key={i}
                        className={cn('size-1 rounded-full', accentBg[color])}
                      />
                    ))}
                  </span>
                </button>
              )
            })}
          </div>
        </div>

        {/* Selected-day agenda */}
        <div
          className={cn(
            'p-3',
            horizontal
              ? 'border-t border-fbui-gray-100 sm:flex-1 sm:border-l sm:border-t-0'
              : 'border-t border-fbui-gray-100'
          )}
        >
          {/* Matches the calendar header band height for a shared baseline. */}
          <p className="mb-1 flex h-7 items-center px-2 text-xs font-semibold uppercase tracking-wide text-fbui-gray-500">
            {relativeDayLabel(startOfDay(selected), resolvedNow)}
          </p>
          {selectedEvents.length > 0 ? (
            <div className="flex flex-col">
              {selectedEvents.map((event) => (
                <AgendaRow
                  key={event.id}
                  event={event}
                  onClick={onEventClick && (() => onEventClick(event))}
                />
              ))}
            </div>
          ) : (
            <p className="px-2 py-3 text-sm text-fbui-gray-500">{emptyLabel}</p>
          )}
        </div>
      </div>
    )
  }
)
EventCalendarWidget.displayName = 'EventCalendarWidget'
